import {
  Asset,
  Button,
  DisplaySelectorCallback,
  FeedData,
  Layout,
  OpenConfirmOptions,
  ThemeAsset,
  EncodingStatusResult,
  UploaderOptions,
  UploadedFileResult,
  ApiDataConnectorOptions,
  DataConnectionConfig,
  AssetPreviewRequest,
  AssetPreviewData,
} from './external-types';
import { BridgeBase } from './bridge/bridge-base';
import { validateArray, validateBool, validateFunction, validateObject, validateString } from './utils';
import { MessageNamespaces, MessageNames } from './internal-types';

/**
 * Dashboard API is a part of the Dashboard SDK. It can be used to present standardized User Interface elements.
 * Use global `enplug.dashboard` object to access these methods.
 *
 * ```typescript title="Example usage of the Dashboard API"
 * enplug.dashboard.pageLoading(false);
 * ```
 *
 * **Useful commands**
 *
 * Header section:
 * - {@link setHeaderTitle|`enplug.dashboard.setHeaderTitle()`} - sets header title
 * - {@link setHeaderButtons|`enplug.dashboard.setHeaderButtons()`} - sets header buttons
 * - {@link setDisplaySelectorCallback|`enplug.dashboard.setDisplaySelectorCallback()`} - sets Display Group selector callback
 * - {@link setDisplaySelectorVisibility|`enplug.dashboard.setDisplaySelectorVisibility()`} - sets Display Group selector visibility
 *
 * Progress indicator:
 * - {@link pageLoading|`enplug.dashboard.pageLoading()`} - sets the app's loader visibility
 * - {@link loadingIndicator|`enplug.dashboard.loadingIndicator()`} - shows **Loading** indicator
 * - {@link successIndicator|`enplug.dashboard.successIndicator()`} - shows **Success** indicator
 * - {@link errorIndicator|`enplug.dashboard.errorIndicator()`} - shows **Error** indicator
 *
 * Dialogs:
 * - {@link previewAsset|`enplug.dashboard.previewAsset()`} - shows asset preview
 * - {@link upload|`enplug.dashboard.upload()`} - shows upload dialog
 * - {@link openConfirm|`enplug.dashboard.openConfirm()`} - shows custom confirmation dialog
 * - {@link confirmUnsavedChanges|`enplug.dashboard.confirmUnsavedChanges()`} - shows unsaved changes dialog
 * - {@link setAppHasUnsavedChanges|`enplug.dashboard.setAppHasUnsavedChanges()`} - sets dashboard's navigation guard
 */
export class Dashboard {
  /**
   * The buttons most recently registered with the dashboard header.
   * Stored locally so that we can respond to click events
   */
  private currentButtons: { [key: string]: Button } = {};

  /**
   * Keeps track of whether the dashboard is loading mode so clients can check.
   */
  private isLoadingCache = true;

  /** @internal */
  constructor(private bridge: BridgeBase) {
    this.bridge.setWindowEventHandler('click', () => {
      this.click();
      return true;
    });
  }

  /**
   * @internal
   * Notifies the parent dashboard of a click in the child iframe. Used to close
   * dropdown windows, etc. which were opened in parent window and are unable to
   * respond to click events in child iframe.
   *
   * Event handler is automatically bound when the SDK is created.
   */
  click(): void {
    this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.Click,
      transient: true,
    });
  }

  /**
   * Opens a confirm window asking the user to confirm their unsaved changes.
   *
   * ```typescript title="Presenting Unsaved Changes dialog"
   * try {
   *   await enplug.dashboard.confirmUnsavedChanges();
   *   // user confirmed - take action
   * } catch {
   *   // user cancelled
   * }
   * ```
   *
   * ![Unsaved Changes dialog](/img/dashboard-sdk/dashboard/dialog-hasunsavedchanges.png)<br />
   * *Unsaved Changes dialog*
   *
   * @returns Resolves if dialog was confirmed, rejects otherwise.
   */
  confirmUnsavedChanges(): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.ConfirmUnsavedChanges,
    });
  }

  /**
   * Shows the error indicator.
   *
   * ```typescript title="Presenting the error indicator"
   * enplug.dashboard.errorIndicator('Asset could not be removed');
   * ```
   *
   * ![Error indicator](/img/dashboard-sdk/dashboard/progress-error.png)
   * *Error indicator*
   *
   * @param message - A message to show in the error toast.
   *
   * @returns Resolves after the error indicator shows up on screen.
   */
  errorIndicator(message: string): Promise<any> {
    validateString(message, 'Error indicator requires an error message (string)');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.ErrorIndicator,
      params: message,
    });
  }

  /**
   * Synchronously returns the current loading state.
   * Updated asynchronously when this sender receives an acknowledgement
   * of successful SDK call from the dashboard.
   *
   * @see {@link pageLoading} to update the page loading state.
   *
   * @returns Current page loading state.
   */
  isLoading(): boolean {
    return this.isLoadingCache;
  }

  /**
   * Turns on the progress indicator, typically used during asynchronous actions.
   *
   * :::note
   * Note that the progress indicator will continue until a call is made to the
   * {@link errorIndicator} or {@link successIndicator} APIs.
   * :::
   *
   * ```typescript title="Presenting the loading indicator"
   * enplug.dashboard.loadingIndicator('Removing asset');
   * ```
   *
   * ![Loading indicator](/img/dashboard-sdk/dashboard/progress-loading.png)
   * *Loading indicator*
   *
   * @param message - A message to show in the loading toast.
   *
   * @returns Resolves after the loading indicator shows up on screen.
   */
  loadingIndicator(message: string): Promise<void> {
    validateString(message, 'Loading indicator requires a loading message (string)');
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.LoadingIndicator,
      params: message,
    });
  }

  /**
   * Navigate to given relative dashboard URL.
   */
  navigateToUrl(url: string): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.NavigateToUrl,
      params: { url },
    });
  }

  /**
   * Navigate back to zoning from widget.
   * @param assetId - optional, fallback parameter for v1-\>v2 transition phase
   */
  navigateToZoning(assetId?: string): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.NavigateToZoning,
      params: { assetId },
    });
  }

  /**
   * Opens a confirm window with Yes/No buttons and configurable messages.
   *
   * ```typescript title="Presenting confirmation dialog"
   * try {
   *   await enplug.dashboard.openConfirm({
   *     title: 'Delete asset?',
   *     text: 'Are you sure you want to delete this asset? This action cannot be undone.',
   *     cancelText: 'Cancel',
   *     confirmText: 'Delete',
   *     confirmClass: 'btn-danger'
   *   });
   *
   *   // remove the asset
   * } catch {
   *   // user cancelled
   * }
   * ```
   *
   * ![Confirm dialog](/img/dashboard-sdk/dashboard/dialog-confirm.png)<br />
   * *Confirm dialog*
   *
   * @param options - Confirm dialog options.
   *
   * @returns Resolves if Confirm button was clicked, rejects otherwise.
   */
  openConfirm(options: OpenConfirmOptions): Promise<any> {
    validateObject(options, 'Confirm box requires options to be set (object).');

    if (options) {
      validateString(options.title, 'Confirm box requires options.title to be set (string).');
      validateString(options.text, 'Confirm box requires options.text to be set (string).');
    }

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.OpenConfirm,
      params: options,
    });
  }

  /**
   * Puts the page into error state.
   *
   * ![Page error](/img/dashboard-sdk/dashboard/page-error.png)<br />
   * *Page error*
   *
   * @returns Resolves after the Dashboard shows the page error.
   */
  pageError(): Promise<void> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.PageError,
    });
  }

  /**
   * Controls the loading state for the entire page. Every application starts off in
   * loading state, and must set `pageLoading(false)` to notify the dashboard that it
   * has successfully loaded.
   *
   * ```typescript title="Turning off the loading spinner after app has started"
   * enplug.dashboard.pageLoading(false);
   * ```
   *
   * ![Page Loading spinner](/img/dashboard-sdk/dashboard/pageloading-spinner.png)<br />
   * *Page Loading spinner*
   *
   * @param isLoading - Page loading state:<br />
   * `false` - hides the loader and shows the app,<br />
   * `true` - shows the loader and hides the app.
   *
   * @returns Resolves after the loading state has been updated by the Dashboard.
   */
  pageLoading(isLoading: boolean): Promise<boolean> {
    validateBool(isLoading, 'Page loading status must be a boolean.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.PageLoading,
      params: isLoading,
    }).then((loading: boolean) => {
      this.isLoadingCache = loading;
      return loading;
    });
  }

  /**
   * Puts the page into not found (404) state.
   *
   * ![Page not available](/img/dashboard-sdk/dashboard/page-404.png)<br />
   * *Page not available*
   *
   * @returns Resolves when after the Dashboard shows the page not found error.
   */
  pageNotFound(): Promise<void> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.PageNotFound,
    });
  }

  /**
   * @deprecated use {@link previewAsset}
   *
   * Opens app preview modal.
   */
  preview<AssetValue, ThemeValue, FeedType>(
    url: string,
    asset: Asset<AssetValue>,
    theme: ThemeAsset<ThemeValue>,
    layout?: Layout,
    feedData?: FeedData<FeedType>): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.Preview,
      params: {
        url,
        asset,
        theme,
        layout,
        feedData,
      },
    });
  }

  /**
   * Opens asset preview modal.
   *
   * ![Asset preview modal](/img/dashboard-sdk/dashboard/previewAsset.png)<br />
   * *Asset preview modal*
   *
   * ```typescript title="Previewing an asset"
   * function showPreview(asset: Asset<AssetModel>) {
   *   enplug.dashboard.previewAsset({ asset });
   * }
   * ```
   *
   * The above code opens the preview dialog with provided data.
   * This method allows many preview customizations, see {@link AssetPreviewRequest}.
   *
   * :::note
   * The app inside of the preview modal is presented in an iframe and the app is provided with basic SDK functionalities
   * needed to display the asset only. The app's Player URL from app's definition will be used.
   * The app is not run in a WebPlayer environment, so the behavior might be different.
   * Most SDK calls will have no effect.
   * :::
   *
   * @param TAssetValue - Model of asset's value
   * @param TThemeValue - Model of theme's value (optional)
   * @param TFeedType - Model of the feed (used for social apps)
   *
   * @returns Resolves after the preview modal gets closed.
   */
  previewAsset<TAssetValue = any, TThemeValue = any, TFeedType = any>(
    request: AssetPreviewRequest<TAssetValue, TThemeValue, TFeedType>): Promise<void> {

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.PreviewAsset,
      params: { ...request },
    });
  }

  /**
   * @internal
   * Get preview parameters for an app asset for custom preview handling.
   * @param origin - (default: true) - custom origin url, when omitted or falsy, `window.location.origin` will be attached
   */
  getAssetPreviewData(appId: string, asset: Asset<any>, origin?: string | boolean): Promise<AssetPreviewData> {
    validateString(appId, 'appId must be a string.');
    validateObject(asset, 'asset must be an instance of Asset<any>.');
    validateString(origin, 'origin must be a string.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.GetAssetPreviewData,
      params: {
        appId,
        asset,
        origin,
      },
    });
  }

  /**
   * Sets the current callback for the title bar breadcrumb Display Group selector dropdown.
   * Attaching a callback enables the dropdown and resets the title.
   *
   * The callback is fired when a Display Group is selected from the dropdown, the callback will get the ID
   * value when a single display is fired, or null when the whole 'Library' selection.
   *
   * ```typescript title="Setting up the Display Group selector"
   * enplug.dashboard.setDisplaySelectorCallback(async () => {
   *   // update your asset list using enplug.account.getAssets() which will return
   *   // the assets assigned to the selected Display Group only
   *   yourAssetList = await enplug.account.getAssets();
   * });
   * ```
   *
   * ![Display Group selector dropdown](/img/dashboard-sdk/dashboard/display-group-selector-dropdown.png)<br />
   * *Display Group selector dropdown*
   *
   * @param callback - A function to be called every time a user changes current Display Group
   * with the dropdown selector. The callback should reload the asset list to get the filtered version.
   *
   * @returns Promise which resolves after the command gets executed by the Dashboard.
   */
  setDisplaySelectorCallback(callback: DisplaySelectorCallback): Promise<void> {
    validateFunction(callback, 'To enable the display selector in the page title you must supply a callback function.');

    this.currentDisplayCallback = callback;

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.SetDisplaySelectorCallback,
      params: callback,
      persistent: true,
    }, (display: string) => {
      if (typeof this.currentDisplayCallback === 'function' && display !== undefined) {
        this.currentDisplayCallback(display);
      }

      return display;
    });
  }

  /**
   * Hides or shows the Display Group dropdown selector in the page title breadcrumb bar.
   * Send true to show the selector, false to hide it.
   *
   * The Display Group selector dropdown can be used to filter assets currently deployed to a specific Display Group.
   * You can view all the saved assets by account for your app by selecting the 'Library' view from the dropdown on the upper left.
   *
   * ![Display Group selector dropdown](/img/dashboard-sdk/dashboard/display-group-selector-dropdown.png)
   * *Display Group selector dropdown*
   *
   * @returns Promise which resolves after the command gets executed by the Dashboard.
   */
  setDisplaySelectorVisibility(show: boolean): Promise<void> {
    validateBool(show, 'Setting the display selector visibility requires a boolean argument, true to show or false to hide.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.SetDisplaySelectorVisibility,
      params: show,
    });
  }

  /**
   * Sets the action buttons for a page in the titlebar.
   *
   * Accepts either a single button object, or an array of buttons.
   * Each button must have a button.action callback.
   *
   * ```typescript title="Updating the buttons"
   * function updateButtons(hasUnsavedChanges: boolean) {
   *   enplug.dashboard.setHeaderButtons([{
   *     text: 'My Signs',
   *     action: () => { \/* your code to navigate back to the Asset list *\/ },
   *     class: 'btn-default'
   *   }, {
   *     text: 'Save',
   *     action: () => { \/* your code saving the asset *\/ },
   *     class: 'btn-primary',
   *     disabled: !hasUnsavedChanges // this will disable the button if there are no unsaved changes
   *   }]);
   * }
   * ```
   *
   * ![Header buttons](/img/dashboard-sdk/dashboard/header-buttons.png)<br />
   * *Header buttons*
   *
   * @param buttons - A button (single or array) to set on a header.
   *
   * @returns Resolves after the buttons get updated.
   */
  setHeaderButtons(buttons: Button[] | Button): Promise<void> {
    if (typeof buttons !== 'object') {
      throw new Error('Header buttons must be an object (single) or array (multiple).');
    }

    // Reset current buttons.
    this.currentButtons = {};

    buttons = Array.isArray(buttons) ? buttons : [buttons];

    for (const button of buttons) {
      validateObject(button, 'Header buttons must be objects.');
      if (button) {
        validateFunction(button.action, 'Header button must have an action (function).');

        // The button ID is used to identify which button was clicked in this service
        button.id = 'button-' + (Math.round(Math.random() * (10000 - 1) + 1));
        this.currentButtons[button.id] = button;
      }
    }

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.SetHeaderButtons,
      params: buttons,
      persistent: true,
    }, (clicked: Button) => {
      if (clicked) {
        const button = this.currentButtons[clicked.id];
        if (button) {
          button.action();
        } else {
          console.warn('Unrecognized button click:', clicked);
        }
      }
      return clicked;
    });
  }

  /**
   * Sets the last part of the title bar breadcrumb.
   * Set an empty title '' to clear the title.
   * Re-setting this value wipes out the old one.
   *
   * The home/default page for an app should have no title set.
   *
   * ```typescript title="Updating header's title"
   * enplug.dashboard.setHeaderTitle('Setup');
   * ```
   *
   * ![Header title](/img/dashboard-sdk/dashboard/header-title.png)
   * *Header title section*
   *
   * @param title - A title to set.
   *
   * @returns Resolves after the title has been modified.
   */
  setHeaderTitle(title: string): Promise<void> {
    validateString(title, 'Header title must be a string.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.SetHeaderTitle,
      params: title,
    });
  }

  /**
   * Shows the success indicator.
   *
   * ```typescript title="Presenting the success indicator"
   * enplug.dashboard.successIndicator('Asset removed');
   * ```
   *
   * ![Success indicator](/img/dashboard-sdk/dashboard/progress-success.png)
   * *Success indicator*
   *
   * @param message - A message to show in the success toast.
   *
   * @returns Resolves after the success indicator shows up on screen.
   */
  successIndicator(message: string): Promise<void> {
    validateString(message, 'Success indicator requires a success message (string)');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.SuccessIndicator,
      params: message,
    });
  }

  /**
   * Switches to account view "Library"/All selection of instance selector or Display Group view.
   */
  switchToView(displayGroupId: string, displayGroupName: string): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.SwitchToView,
      params: {
        displayGroupId,
        displayGroupName,
      },
    });
  }

  /**
   * Uses upload interface and Enplug encoding service, returns uploaded object
   * @param options - Filepicker options
   */
  upload(options: UploaderOptions): Promise<UploadedFileResult[]> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.Upload,
      params: options,
    });
  }

  /**
   * Uses connect data interface, returns connection configuration object.
   * @param options - API Data Connector configuration options.
   */
  connectData(options: ApiDataConnectorOptions | ApiDataConnectorOptions[]): Promise<DataConnectionConfig> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.ConnectData,
      params: options,
    });
  }


  /**
   * @internal
   * Returns current encoding status of an asset.
   * @deprecated legacy FileStack upload flow
   * @see resourcesEncodingStatus
   */
  encodeStatus(url: string): Promise<any> {
    validateString(url, 'You must provide the url (string).');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.EncodeStatus,
      params: url,
    });
  }

  /**
   * Returns current encoding statuses of resources or asset IDs.
   * @param byAssetId - If `true`, the provided IDs are interpreted as asset IDs, otherwise they are resource IDs.
   */
  resourcesEncodingStatus(ids: string[], byAssetId = false): Promise<EncodingStatusResult[]> {
    validateArray(ids, 'You must provide an array of ids (strings).');
    validateBool(byAssetId, 'You must provide the boolean byAssetId. True to interpret the ids as asset ids, when false as resource ids.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.ResourcesEncodingStatus,
      params: {
        ids,
        byAssetId,
      },
    });
  }

  /**
   * Opens content interval settings dialog for Ad Scheduler app.
   */
  openContentIntervalSettingsDialog(appName: string, level: string, levelId: string): Promise<any> {
    validateString(appName, 'You must provide the appName (string).');
    validateString(level, 'You must provide the level (string).');
    validateString(levelId, 'You must provide the levelId (string).');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.OpenContentIntervalSettingsDialog,
      params: {
        appName,
        level,
        levelId,
      },
    });
  }

  /**
   * Opens dialog to copy templates across different accounts
   */
  openCopyTemplatesDialog<T>(appId: string, assets: Array<Asset<T>>): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.OpenCopyTemplatesDialog,
      params: {
        appId,
        assets,
      },
    });
  }

  /**
   * Lets dashboard know that the state of user changes in the app changed.
   *
   * @param hasUnsavedChanges - Whether the app has unsaved changes.
   */
  setAppHasUnsavedChanges(hasUnsavedChanges: boolean): Promise<any> {
    validateBool(hasUnsavedChanges, 'To report changes to the dashboard you must supply a value.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.SetAppHasUnsavedChanges,
      transient: true,
      params: {
        hasUnsavedChanges,
      },
    });
  }

  /**
   * Creates new assets and begins encoding process
   */
  processAssetResource<T>(accountId: string, appId: string, assets: Array<Asset<T>>): Promise<any> {
    validateString(accountId, 'You must provide the accountId (string).');
    validateString(appId, 'You must provide the appId (string).');
    validateArray(assets, 'You must provide the assets (array).');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.ProcessAssetResource,
      params: {
        accountId,
        appId,
        assets,
      },
    });
  }

  /**
   * The last callback registered with the dashboard title bar.
   */
  private currentDisplayCallback: DisplaySelectorCallback = (displayId: string) => { };

  /**
   * Sends a boolean to know when to show or hide the Administrative Screen Breadcrumb
  */
  showAdministrativeBreadcrumb(showBreadcrumb: boolean): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.ShowAdministrativeBreadcrumb,
      params: {
        showBreadcrumb,
      },
    });
  }

  /**
   * Sends a string to know which type of breadcrumb we are going to display in the dashboard
  */
  breadCrumbType(breadCrumbType: string): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.Dashboard,
      name: MessageNames.BreadCrumbType,
      params: {
        breadCrumbType,
      },
    });
  }
}
